import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PageHeader from "@/components/base/PageHeader";
import Card from "@/components/base/Card";
import Button from "@/components/base/Button";
import Badge from "@/components/base/Badge";
import Modal from "@/components/base/Modal";
import { EmptyState, ErrorState, LoadingState } from "@/components/base/StateViews";
import { useToast } from "@/components/base/Toast";
import ReasonModal from "@/components/feature/ReasonModal";
import { useAuth } from "@/contexts/AuthContext";
import { useAsync } from "@/hooks/useAsync";
import {
  closeMonth,
  getClosingHistory,
  getClosings,
  reopenMonth,
  type ClosingEvent,
  type MonthClosing,
} from "@/services/closingService";
import { getActiveSchoolYear } from "@/services/schoolService";
import { formatDate, formatMoney, plural } from "@/utils/format";

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** Clôtures mensuelles : un mois vérifié est verrouillé ; seul l'Administrateur peut le rouvrir. */
export default function ClosingsPage() {
  const { notify } = useToast();
  const { can, role } = useAuth();
  const isAdmin = role?.isSystem === true;
  const canClose = can("closings:create");
  const schoolYear = getActiveSchoolYear();

  const [toClose, setToClose] = useState<MonthClosing | null>(null);
  const [closing, setClosing] = useState(false);
  const [toReopen, setToReopen] = useState<MonthClosing | null>(null);
  const [historyOf, setHistoryOf] = useState<MonthClosing | null>(null);
  const [history, setHistory] = useState<ClosingEvent[] | null>(null);
  const { data, loading, error, reload } = useAsync(() => getClosings(schoolYear), [schoolYear]);

  useEffect(() => {
    if (!historyOf) return;
    setHistory(null);
    getClosingHistory(historyOf.month)
      .then(setHistory)
      .catch(() => setHistory([]));
  }, [historyOf]);

  if (loading && !data) return <LoadingState label="Chargement des clôtures..." />;
  if (error || !data) return <ErrorState message={error ?? "Chargement impossible."} onRetry={reload} />;

  const confirmClose = async () => {
    if (!toClose) return;
    setClosing(true);
    try {
      await closeMonth(toClose.month);
      notify(`${capitalize(toClose.label)} est clôturé.`);
      setToClose(null);
      reload();
    } catch (err) {
      notify(err instanceof Error ? err.message : "Clôture impossible.", "error");
    } finally {
      setClosing(false);
    }
  };

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Clôtures mensuelles"
        subtitle={`Mois vérifiés et verrouillés — année ${schoolYear}`}
        icon="ri-lock-2-line"
      />

      <p className="mb-4 rounded-md border border-background-200 bg-background-100/60 px-4 py-3 text-sm text-foreground-600">
        Un mois clôturé ne peut plus recevoir ni reçu, ni recette, ni dépense, ni paie, et rien de ce qui y est daté ne peut
        être modifié ou annulé. Ses rapports deviennent définitifs. Seul un administrateur peut rouvrir un mois, avec un motif.
      </p>

      <Card padded={false}>
        {data.length === 0 ? (
          <div className="p-6">
            <EmptyState icon="ri-calendar-line" title="Aucun mois à afficher" message="L'année scolaire n'a pas encore commencé." />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] text-sm">
              <thead className="bg-background-100 text-left text-xs uppercase tracking-wide text-foreground-500">
                <tr>
                  <th className="px-4 py-2.5 font-medium">Mois</th>
                  <th className="px-3 py-2.5 font-medium text-right">Recettes</th>
                  <th className="px-3 py-2.5 font-medium text-right">Dépenses</th>
                  <th className="px-3 py-2.5 font-medium text-right">Solde</th>
                  <th className="px-3 py-2.5 font-medium text-right">Reçus</th>
                  <th className="px-3 py-2.5 font-medium">Caisses</th>
                  <th className="px-3 py-2.5 font-medium">État</th>
                  <th className="px-4 py-2.5 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.map((m) => (
                  <tr key={m.month} className="border-t border-background-200">
                    <td className="px-4 py-3 font-medium text-foreground-900">{capitalize(m.label)}</td>
                    <td className="px-3 py-3 text-right text-primary-700">{formatMoney(m.revenues)}</td>
                    <td className="px-3 py-3 text-right text-accent-700">{formatMoney(m.expenses)}</td>
                    <td className={`px-3 py-3 text-right font-semibold ${m.balance < 0 ? "text-accent-700" : "text-foreground-900"}`}>
                      {formatMoney(m.balance)}
                    </td>
                    <td className="px-3 py-3 text-right">{m.receipts}</td>
                    <td className="px-3 py-3">
                      {m.pendingCashDays.length === 0 ? (
                        <span className="text-xs text-foreground-500">Toutes validées</span>
                      ) : (
                        <span
                          className="text-xs text-accent-700"
                          title={m.pendingCashDays.map((d) => formatDate(d)).join(", ")}
                        >
                          {plural(m.pendingCashDays.length, "jour non validé", "jours non validés")}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      {m.status === "CLOSED" ? (
                        <>
                          <Badge tone="secondary" icon="ri-lock-2-line">
                            Clôturé
                          </Badge>
                          <p className="mt-1 text-xs text-foreground-500">
                            {m.closedByName}, {m.closedAt ? formatDate(m.closedAt) : ""}
                          </p>
                        </>
                      ) : (
                        <Badge tone="neutral" icon="ri-lock-unlock-line">
                          Ouvert
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1.5">
                        {m.status === "OPEN" && canClose && (
                          <Button size="sm" icon="ri-lock-2-line" onClick={() => setToClose(m)}>
                            Clôturer
                          </Button>
                        )}
                        {m.status === "CLOSED" && isAdmin && (
                          <Button size="sm" variant="danger" icon="ri-lock-unlock-line" onClick={() => setToReopen(m)}>
                            Rouvrir
                          </Button>
                        )}
                        {(m.status === "CLOSED" || m.reopenCount > 0) && (
                          <Button size="sm" variant="ghost" icon="ri-history-line" onClick={() => setHistoryOf(m)} title="Historique" />
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal
        open={toClose !== null}
        onClose={() => setToClose(null)}
        title={`Clôturer ${toClose?.label ?? ""}`}
        icon="ri-lock-2-line"
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setToClose(null)}>
              Annuler
            </Button>
            <Button icon="ri-lock-2-line" onClick={confirmClose} disabled={closing}>
              {closing ? "Clôture..." : "Clôturer le mois"}
            </Button>
          </>
        }
      >
        {toClose && (
          <div className="space-y-3 text-sm text-foreground-600">
            <p>
              Recettes {formatMoney(toClose.revenues)}, dépenses {formatMoney(toClose.expenses)}, solde{" "}
              <strong>{formatMoney(toClose.balance)}</strong>. Après la clôture, plus rien de daté de ce mois ne pourra être
              ajouté, modifié ou annulé.
            </p>
            {toClose.pendingCashDays.length > 0 && (
              <p className="rounded-md bg-accent-50 px-3 py-2 text-xs text-accent-800">
                <i className="ri-error-warning-line"></i> {plural(toClose.pendingCashDays.length, "journée")} de caisse n'ont pas
                été comptées : {toClose.pendingCashDays.map((d) => formatDate(d)).join(", ")}. Vous pouvez les{" "}
                <Link to="/dashboard/cash" className="underline">
                  valider d'abord
                </Link>
                , car après la clôture elles ne pourront plus l'être.
              </p>
            )}
          </div>
        )}
      </Modal>

      <ReasonModal
        open={toReopen !== null}
        title={`Rouvrir ${toReopen?.label ?? ""}`}
        message="Le mois redeviendra modifiable. Pensez à le clôturer à nouveau après la correction."
        confirmLabel="Rouvrir le mois"
        onClose={() => setToReopen(null)}
        onConfirm={async (reason) => {
          await reopenMonth(toReopen!.month, reason);
          notify(`${capitalize(toReopen!.label)} est rouvert.`);
          reload();
        }}
      />

      <Modal
        open={historyOf !== null}
        onClose={() => setHistoryOf(null)}
        title="Historique de la clôture"
        subtitle={historyOf ? capitalize(historyOf.label) : undefined}
        icon="ri-history-line"
        size="sm"
      >
        {history === null ? (
          <p className="text-sm text-foreground-500">Chargement...</p>
        ) : (
          <ul className="divide-y divide-background-200 rounded-md border border-background-200">
            {history.map((e) => (
              <li key={e.id} className="px-4 py-3">
                <p className="text-sm font-medium text-foreground-900">
                  {e.action === "CLOSE" ? "Clôturé" : "Rouvert"} par {e.byName}
                </p>
                <p className="text-xs text-foreground-500">{formatDate(e.createdAt)}</p>
                {e.reason && <p className="mt-1 text-xs text-foreground-700">Motif : {e.reason}</p>}
              </li>
            ))}
          </ul>
        )}
      </Modal>
    </div>
  );
}
